/**
 * Utility functions for SEO ID extraction from pathname
 */

/**
 * Extract SEO ID from pathname based on specific rules
 * @param {string} pathname - The current pathname
 * @returns {string|null} The SEO ID to use for API call, or null if no match
 */
export function extractSeoId(pathname) {
  if (!pathname) return null;

  // Remove query parameters and hash
  const cleanPath = pathname.split('?')[0].split('#')[0];
  
  // Remove leading and trailing slashes
  const normalizedPath = cleanPath.replace(/^\/+|\/+$/g, '');
  
  // Handle root path
  if (normalizedPath === '' || normalizedPath === '/') {
    return 'home';
  }

  // Split path into segments
  const segments = normalizedPath.split('/').filter(Boolean);

  // Handle specific patterns
  // /freelancer-profile/* -> freelancer-profile (ignore slug after)
  if (segments[0] === 'freelancer-profile') {
    return 'freelancer-profile';
  }

  // /freelancers-list -> freelancers-list (ignore query params)
  if (segments[0] === 'freelancers-list') {
    return 'freelancers-list';
  }

  // /services/* -> services (second segment, ignore rest)
  if (segments[0] === 'services') {
    return 'services';
  }

  // /servicelist/*/freelancers -> second segment (e.g., web-developers-34)
  if (segments[0] === 'servicelist' && segments.length >= 2) {
    return segments[1]; // e.g., web-developers-34
  }

  // /categories/*/makeup-artists-2 -> last segment (e.g., makeup-artists-2)
  if (segments[0] === 'categories' && segments.length >= 3) {
    return segments[segments.length - 1]; // Last segment
  }

  // /categories/* -> second segment (e.g., wedding-events-2)
  if (segments[0] === 'categories' && segments.length === 2) {
    return segments[1]; // e.g., wedding-events-2
  }

  // For simple paths like /about-us, /contact-us, etc., use the first segment
  if (segments.length === 1) {
    return segments[0];
  }

  // Default: return null if no pattern matches
  return null;
}

/**
 * Check if a pathname should have SEO data fetched
 * @param {string} pathname - The current pathname
 * @returns {boolean} Whether to fetch SEO data for this path
 */
export function shouldFetchSeo(pathname) {
  if (!pathname) return false;

  // Don't fetch SEO for admin routes
  if (pathname.startsWith('/control') || pathname.startsWith('/(superadmin)')) {
    return false;
  }

  // Don't fetch SEO for dashboard routes (they might have their own SEO)
  if (pathname.startsWith('/freelancer-dashboard') || pathname.startsWith('/customer-dashboard')) {
    return false;
  }

  // Don't fetch SEO for auth routes that don't need dynamic SEO
  if (pathname.startsWith('/registration') || pathname.startsWith('/select-user-type')) {
    return false;
  }

  // Don't fetch SEO for maintenance page
  if (pathname === '/maintenance') {
    return false;
  }

  const seoId = extractSeoId(pathname);
  return seoId !== null;
}

